// Root route — owns the global layout.
//
// Why this lives here (not in App.tsx):
//   - TanStack Router treats the root route as the layout owner. Every
//     screen renders below its <Outlet />, so the AppShell + cosmic
//     backdrop + global AppHeader are painted once here and inherited
//     by /, /login, /account, and the $ catchall alike.
//   - The 'auth:unauthorized' CustomEvent is dispatched by api-client.ts
//     whenever a request returns 401. Listening at the root lets us
//     trigger a client-side navigation to /login instead of the
//     api-client's hard window.location fallback, surface a user-facing
//     toast explaining why the session ended, and re-affirm the auth
//     store clear (idempotent — api-client clears too, this guards
//     against any external dispatcher that forgets to). The listener
//     attaches once per mount and tears down cleanly on unmount.
//   - queryClient is threaded via createRootRouteWithContext so loaders
//     and beforeLoad guards on child routes can read `context.queryClient`
//     without re-importing the singleton.
//   - errorComponent at the root catches any uncaught error thrown from
//     a loader/component below it, so a broken child route still renders
//     the AppShell + a friendly recovery card instead of a white screen.
import { useEffect } from 'react'
import {
  createRootRouteWithContext,
  Outlet,
  useNavigate,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { Toaster } from '@/components/ui/sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  AUTH_UNAUTHORIZED_EVENT,
  refreshAuthSession,
} from '@/lib/api-client'
import { AUTH_STORAGE_KEY, useAuthStore } from '@/stores/auth-store'

export interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: RootErrorBoundary,
})

function RootComponent() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  useEffect(() => {
    // api-client dispatches this event on any 401. The router root is
    // the single owner of "where do I send the user when their session
    // dies":
    //   1. Re-clear the auth store. api-client already calls clear()
    //      before dispatching, so this is idempotent — but it also
    //      catches any future caller that dispatches the event from
    //      outside the api-client without clearing first.
    //   2. Surface a toast so the user understands WHY they were just
    //      bounced back to /login. Without this, the redirect feels
    //      like a glitch.
    //   3. Navigate client-side to /login. Using TanStack's navigate
    //      keeps the SPA in-band instead of bouncing through
    //      window.location, and the api-client's 50ms hard-redirect
    //      fallback only fires if this listener didn't move the URL.
    const handler = (): void => {
      try {
        useAuthStore.getState().clear()
      } catch {
        // Auth store hydration race — safe to ignore. api-client also
        // clears, so the session is still gone.
      }
      queryClient.clear()
      toast.error('Your session expired — please sign in again.')
      void navigate({ to: '/login', replace: true })
    }
    if (typeof window !== 'undefined') {
      window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handler)
      return () => {
        window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handler)
      }
    }
    return undefined
  }, [navigate, queryClient])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    let lastActivityAt = 0
    const handleActivity = (event: Event): void => {
      if (!event.isTrusted || document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastActivityAt < 10_000) return
      lastActivityAt = now
      const before = useAuthStore.getState()
      void refreshAuthSession().then((refreshed) => {
        const after = useAuthStore.getState()
        if (refreshed && after.sessionId === before.sessionId && after.token !== before.token) {
          void queryClient.invalidateQueries()
        }
      })
    }

    const handleStorage = (event: StorageEvent): void => {
      if (event.key !== AUTH_STORAGE_KEY) return
      const previousSessionId = useAuthStore.getState().sessionId
      void Promise.resolve(useAuthStore.persist.rehydrate()).then(() => {
        const session = useAuthStore.getState()
        if (previousSessionId !== session.sessionId) {
          queryClient.clear()
        }
        if (previousSessionId && session.token === null) {
          void navigate({ to: '/login', replace: true })
        }
      })
    }

    // Start renewal before React handlers submit protected mutations.
    window.addEventListener('pointerdown', handleActivity, { passive: true, capture: true })
    window.addEventListener('keydown', handleActivity, { capture: true })
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('pointerdown', handleActivity, true)
      window.removeEventListener('keydown', handleActivity, true)
      window.removeEventListener('storage', handleStorage)
    }
  }, [navigate, queryClient])

  return (
    <AppShell>
      <Outlet />
      <Toaster />
    </AppShell>
  )
}

function RootErrorBoundary({ error, reset }: ErrorComponentProps) {
  const message =
    error instanceof Error ? error.message : 'Something went wrong.'
  return (
    <main className="mx-auto w-full max-w-xl px-4 py-12 sm:px-6">
      <Card className="w-full">
        <CardHeader className="space-y-2">
          <h1 className="text-2xl font-semibold leading-tight">Something went wrong</h1>
          <p className="break-words text-sm text-muted-foreground">{message}</p>
        </CardHeader>
        <CardContent>
          <Button onClick={reset}>Try again</Button>
        </CardContent>
      </Card>
    </main>
  )
}
